import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EnvironmentConfig, BaseApiService, AuthenticationService, LogService, GuidUtils } from "../../core/core";
import { InspectionDefinition, InspectionInput, InspectionMediaResponse, InspectionStep } from "../models/inspection-definition.model";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import { map, mergeMap, tap } from "rxjs/operators";
import { DatePipe } from "@angular/common";

declare var environment: EnvironmentConfig;

@Injectable({
  providedIn: "root",
})
export class InspectionApiService extends BaseApiService<InspectionDefinition> {
  mediaUploadStatus$ = new BehaviorSubject<{ count: number; total: number }>(null);
  mediaUploadCount: number = 1;

  constructor(httpClient: HttpClient, authenticationService: AuthenticationService, private logService: LogService) {
    super(httpClient, environment.inspectionsApiDomain, "/api/inspections/v1/inspection", true, authenticationService);
  }

  uploadInspection(inspection: InspectionDefinition): Observable<any> {
    this.mediaUploadCount = 1;
    this.mediaUploadStatus$.next(null);

    const inspectionId = inspection.id;
    inspection.id = null;
    delete inspection["localMetadata"];

    // we can remove this once we add input types
    try {
      if (isNaN(+inspection?.inventoryItem?.year)) {
        this.logService.info(this.constructor.name, `Invalid machine year, setting to 0. Year entered: ${inspection?.inventoryItem?.year}`);
        inspection.inventoryItem.year = 0;
      }
    } catch (e) {
      if (inspection.inventoryItem) {
        this.logService.error(this.constructor.name, new Error(`Invalid machine year, setting to 1`));
        inspection.inventoryItem.year = 1;
      } else {
        this.logService.info(this.constructor.name, new Error("Inventory item is null, not setting year to 1"));
      }
    }

    return this.create(inspection).pipe(
      map((response: { id: string }) => {
        // upload the media here
        this.logService.info(this.constructor.name, `created inspection with id '${response.id}. uploading media now'`);
        return this.uploadAllMedia(response.id, inspection);
      }),
      mergeMap((results: Observable<InspectionMediaResponse>[]) => {
        return results.length ? forkJoin(results) : of([]);
      }),
      map((mediaResponses: InspectionMediaResponse[]) => {
        return { inspectionId, inspectionType: inspection.inspectionType };
      }),
    );
  }

  uploadAllMedia(inspectionid: string, inspection: InspectionDefinition): Observable<InspectionMediaResponse>[] {
    const allSteps = inspection.sections.map(d => d.inspectionSteps);
    const flattenedSteps = [].concat.apply([], allSteps) as InspectionStep[];

    let photoInputs = [] as InspectionInput[];
    for (const step of flattenedSteps) {
      // if we have a stored image and  the input type is photo, lets upload it
      const mediaToUpload = step.inputs.filter(d => (d.storedImage != null && d.inputType === "Photo") || (d.videoStorage && d.inputType === "Video"));
      if (mediaToUpload.length > 0) {
        photoInputs = photoInputs.concat(mediaToUpload);
      }
    }

    // go through each of the input photos and create the http requests
    const mediaTotal = photoInputs.length;
    const inputRequests = [] as Observable<InspectionMediaResponse>[];
    for (const input of photoInputs) {
      inputRequests.push(this.uploadMedia(inspectionid, input.stepId, input.id, input.storedImage, input.videoStorage, mediaTotal));
    }

    return inputRequests;
  }

  uploadMedia(inspectionId: string, stepId: string, inputId: string, storedImage, videoStorage, total: number): Observable<InspectionMediaResponse> {
    const headers = this.getAuth();

    const uploadData = new FormData();
    if (storedImage) {
      // we only support jpgs. if we need to support more images then we need to update image preview component
      uploadData.append("file", new File([storedImage], `${GuidUtils.createGuid()}.jpg`));
    }

    if (videoStorage) {
      uploadData.append("file", new File([videoStorage], `${GuidUtils.createGuid()}.webm`));
    }

    return this.httpClient
      .post<InspectionMediaResponse>(`${this.url}/${inspectionId}/step/${stepId}/media?inputId=${inputId}`, uploadData, {
        headers,
      })
      .pipe(
        tap(mediaResponse => {
          if (this.mediaUploadCount === total) {
            this.mediaUploadCount = 1;
            this.mediaUploadStatus$.next(null);
          } else {
            this.mediaUploadCount++;
            this.mediaUploadStatus$.next({ count: this.mediaUploadCount, total: total });
          }
        }),
        map(mediaResponse => {
          return mediaResponse;
        }),
      );
  }

  downloadAllInspectionPhotos(inspectionId: string) {
    const headers = this.getAuth();

    const requestOptions: Object = {
      headers,
      responseType: "arraybuffer",
    };

    return this.httpClient.get<any>(`${this.baseUrl}${this.endpoint}/${inspectionId}/image/archive`, requestOptions).pipe(
      map(result => {
        let blob = new Blob([result], { type: "application/zip" });
        let url = window.URL.createObjectURL(blob);
        window.open(url);
      }),
    );
  }

  inspectionStatusUpdate(inspectionId: string, newStatus: string): Observable<any> {
    const headers = this.getAuth();
    let request = [{ op: "replace", path: "/status", value: `${newStatus}` }];
    return this.httpClient.patch<any>(`${this.baseUrl}/api/inspections/v1/inspection/${inspectionId}?partitionKey=Inspection`, request, { headers });
  }
}
